import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { seoData, SITE } from "@/i18n/seo";

/**
 * Per-page SEO tags (react-helmet-async).
 * pageKey ∈ {home, hasna, services, seniors, family, events, contact}
 * Optional image override for OG.
 */
export default function SEO({ pageKey, image }) {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const data = seoData[lang]?.[pageKey] || seoData.fr[pageKey] || seoData.fr.home;

  const fullUrl = `${SITE.baseUrl}${pathname === "/" ? "" : pathname}`;
  const ogImage = image || SITE.defaultImage;
  const localeMap = { fr: "fr_FR", en: "en_US", ar: "ar_MA" };

  return (
    <Helmet>
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      <title>{data.title}</title>
      <meta name="description" content={data.description} />
      <meta name="keywords" content={data.keywords} />
      <link rel="canonical" href={fullUrl} />

      {/* Hreflang — same URL, page-agnostic language switching */}
      <link rel="alternate" hrefLang="fr" href={fullUrl} />
      <link rel="alternate" hrefLang="en" href={fullUrl} />
      <link rel="alternate" hrefLang="ar" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={data.title} />
      <meta property="og:description" content={data.description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={localeMap[lang] || "fr_FR"} />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:locale:alternate" content="ar_MA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={data.title} />
      <meta name="twitter:description" content={data.description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
